import { createRoute, redirect } from '@tanstack/react-router';
import ClientDetailsPage from '@/features/dashboard/clients/pages/ClientDetailsPage.tsx';
import ClientTabMenu from '@/features/dashboard/clients/pages/ClientTabMenu.tsx';
import ClientInfoTab from '@/features/dashboard/clients/pages/ClientInfoTab.tsx';
import BookingBillTab from '@/features/dashboard/bookingChanges/pages/BookingBillTab.tsx';
import ClientDetailsSkeleton from '@/features/dashboard/clients/components/ClientDetailsSkeleton.tsx';
import DetailsErrorPage from '@/features/dashboard/bookingChanges/components/DetailsErrorPage.tsx';
import { appRoute } from '@/features/dashboard/_root/routes/appRoute.tsx';
import { isMobileViewport } from '@/hooks/use-mobile.tsx';
import { queryClient } from '@/App.tsx';
import { getClientDetailsQueryOptions } from '@/features/dashboard/clients/hooks/useGetClientDetails.ts';
import { getClientBookingDetailsQueryOptions } from '@/features/dashboard/clients/hooks/useGetClientBookingDetails.ts';
import { getTourQueryOptions } from '@/features/dashboard/tours/hooks/useGetTour.ts';
import { getBookingBillQueryOptions } from '@/features/dashboard/bookings/hooks/useGetBookingBill.ts';
import { getBookingDetailsQueryOptions } from '@/features/dashboard/bookings/hooks/useGetBookingDetails.ts';
import { getOpenDraftQueryOptions } from '@/features/dashboard/bookingChanges/hooks/useGetOpenDraft.ts';
import { detailsSearchSchema } from '@/features/dashboard/bookingChanges/routes/detailsSearch.ts';
import { ClientDetailsTab } from '@/features/dashboard/bookingChanges/routes/detailsTabs.ts';

/**
 * Ekran osoby zapisującej. Route nadrzędny to szkielet, każda zakładka ma
 * własny segment ścieżki; `?mode=` zostaje w adresie, bo tryb edycji dotyczy
 * całego ekranu.
 *
 * Loader dociąga też rachunek, szkic i metryczkę rezerwacji, więc nieudane
 * pobranie kończy się `errorComponent`, a nie pustym stanem w środku zakładki.
 */
export const clientRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/clients/$clientId',
  component: ClientDetailsPage,
  validateSearch: detailsSearchSchema,
  loader: async ({ params }) => {
    const { clientId } = params;

    const client = await queryClient.fetchQuery(
      getClientDetailsQueryOptions(clientId),
    );

    const tourId = client.tourIds[0];

    const [tours, bookingDetails] = await Promise.all([
      Promise.all(
        client.tourIds.map((id) =>
          queryClient.fetchQuery(getTourQueryOptions(id)),
        ),
      ),
      tourId
        ? queryClient.ensureQueryData(
            getClientBookingDetailsQueryOptions(clientId, tourId),
          )
        : null,
    ]);

    /*
     * Cały ekran stoi na rachunku rezerwacji, więc klient bez rezerwacji
     * w danym wyjeździe nie ma czego pokazać — tak samo jak uczestnik spoza
     * rezerwacji (`participantRoute`). Co takiemu ekranowi wyświetlić zamiast
     * błędu, jest osobną decyzją produktową.
     */
    const bookingId = bookingDetails?.bookingId;
    if (!bookingId) {
      throw new Error(
        `Zapisujący ${clientId} nie ma rezerwacji w tym wyjeździe`,
      );
    }

    const [booking] = await Promise.all([
      queryClient.ensureQueryData(getBookingDetailsQueryOptions(bookingId)),
      queryClient.ensureQueryData(getBookingBillQueryOptions(bookingId)),
      queryClient.ensureQueryData(getOpenDraftQueryOptions(bookingId)),
    ]);

    const billClientId = booking.client.id;

    return { client, tours, bookingId, billClientId };
  },
  pendingComponent: ClientDetailsSkeleton,
  errorComponent: DetailsErrorPage,
});

/**
 * Adres bez segmentu zakładki — wiersze tabel i linki sprzed tej zmiany.
 * Szeroko prowadzi na „Informacje”, poniżej `md` jest ekranem z kaflami.
 */
export const clientIndexRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: '/',
  beforeLoad: ({ params, search }) => {
    if (isMobileViewport()) return;

    throw redirect({
      to: '/app/clients/$clientId/info',
      params,
      search,
      replace: true,
    });
  },
  component: ClientTabMenu,
});

export const clientInfoRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: ClientDetailsTab.INFO,
  component: ClientInfoTab,
});

export const clientBookingRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: ClientDetailsTab.BOOKING,
  component: BookingBillTab,
});
